import { useEffect, useRef, useState } from "react";
import { stats } from "@/mocks/homeData";

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({ value, label, index }: { value: string; label: string; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const numericPart = parseInt(value.replace(/[^0-9]/g, ""), 10);
  const suffix = value.replace(/[0-9,]/g, "");
  const animated = useCountUp(numericPart, 1800, visible);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center text-center px-8 py-6"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div
        className="text-4xl md:text-5xl font-bold text-gray-900 mb-2"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {visible ? `${animated.toLocaleString()}${suffix}` : "0"}
      </div>
      <div className="text-sm text-gray-500 font-medium tracking-wide">{label}</div>
    </div>
  );
}

export default function StatsSection() {
  return (
    <section className="bg-amber-50 border-y border-amber-100 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-amber-200">
          {stats.map((s, i) => (
            <StatCard key={s.label} value={s.value} label={s.label} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
